
import React, { useRef, useState, useImperativeHandle, forwardRef, useEffect } from "react";
// Prevent multiple rapid re-invocations of arrange

import { DPI, SHEET_WIDTH_IN, SHEET_HEIGHT_IN, PPU_X, PPU_Y, PHYS_UNIT } from '../../config/dtfConfig';
import { PNG } from 'pngjs/browser'; // for embedding DPI metadata
import { changeDpiDataUrl } from 'changedpi';
import { getStorage, ref as storageRef, getDownloadURL, uploadString, deleteObject } from 'firebase/storage';
import { MaxRectsPacker } from 'maxrects-packer';
import { getAuth } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase"; // adjust path if needed
import ImageControlPanel from "./ImageControlPanel";
import "./ImageArranger.css";
import { Box, Button, Typography } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { roundMoney, roundSize } from '../../utils/money';

// Try to derive a human-friendly name from a Firebase Storage URL
function nameFromFirebaseUrl(u) {
  try {
    if (!u || typeof u !== 'string') return null;
    if (u.startsWith('gs://')) {
      const parsed = new URL(u);
      const path = parsed.pathname.replace(/^\//, '');
      const seg = path.split('/').pop();
      return seg || null;
    }
    if (u.includes('firebasestorage.googleapis.com')) {
      const parsed = new URL(u);
      const parts = parsed.pathname.split('/');
      const oIndex = parts.indexOf('o');
      const encodedPath = parts[oIndex + 1] || '';
      const fullPath = decodeURIComponent(encodedPath);
      const seg = fullPath.split('/').pop();
      return seg || null;
    }
    return null;
  } catch (_) {
    return null;
  }
}

// --- Mobile detection helpers (for conditional lighter code paths) ---
const isMobile = () => {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};
const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};


// Cache for pre-rendered image canvases (to avoid redundant draw+rotate)
const imageCache = new Map();

// --- Transparent border trim helper ---
function trimTransparentBorder(imgEl, alphaThreshold = 10) {
  try {
    const w = imgEl.naturalWidth || imgEl.width;
    const h = imgEl.naturalHeight || imgEl.height;
    if (!w || !h) return { changed: false };
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const x = c.getContext('2d');
    x.drawImage(imgEl, 0, 0);
    const { data } = x.getImageData(0, 0, w, h);
    let top = 0, left = 0, right = w - 1, bottom = h - 1;
    const isOpaqueAt = (px) => data[px + 3] > alphaThreshold;
    // scan top
    for (; top <= bottom; top++) {
      let found = false; const row = top * w * 4;
      for (let col = 0; col < w; col++) { if (isOpaqueAt(row + col * 4)) { found = true; break; } }
      if (found) break;
    }
    // scan bottom
    for (; bottom >= top; bottom--) {
      let found = false; const row = bottom * w * 4;
      for (let col = 0; col < w; col++) { if (isOpaqueAt(row + col * 4)) { found = true; break; } }
      if (found) break;
    }
    // scan left
    for (; left <= right; left++) {
      let found = false;
      for (let row = top; row <= bottom; row++) {
        const px = row * w * 4 + left * 4; if (isOpaqueAt(px)) { found = true; break; }
      }
      if (found) break;
    }
    // scan right
    for (; right >= left; right--) {
      let found = false;
      for (let row = top; row <= bottom; row++) {
        const px = row * w * 4 + right * 4; if (isOpaqueAt(px)) { found = true; break; }
      }
      if (found) break;
    }
    const cropW = Math.max(0, right - left + 1);
    const cropH = Math.max(0, bottom - top + 1);
    if (cropW <= 0 || cropH <= 0 || (cropW === w && cropH === h)) return { changed: false };
    const out = document.createElement('canvas');
    out.width = cropW; out.height = cropH;
    out.getContext('2d').drawImage(c, left, top, cropW, cropH, 0, 0, cropW, cropH);
    return { changed: true, canvas: out, crop: { left, top, width: cropW, height: cropH }, orig: { width: w, height: h } };
  } catch (e) {
    console.warn('trimTransparentBorder failed', e);
    return { changed: false };
  }
}

// Stub for WebGL-based arrangement (for massive layouts)
async function arrangeWithWebGL(images, config) {
  console.warn("Using WebGL arrangement for large image sets (not fully implemented).");
  // TODO: implement WebGL-based packing and rendering using OffscreenCanvas('webgl')
  return { previewImages: [], totalCanvasArea: 0 };
}

// Utility to round to hundredth

const PAGE_WIDTH = SHEET_WIDTH_IN * DPI; // 22in × 300 DPI = 6600px

const PAGE_HEIGHT = SHEET_HEIGHT_IN * DPI;

const MARGIN = 1;
const GAP = 225;
const SCALE_FACTOR = 1; // Define a single variable for scaling (adjust for preview size)

// ---- Fallback thresholds & helpers ----
const FALLBACK_INSTANCE_THRESHOLD = 220; // if total item instances exceed this, use safe mode
const FALLBACK_PIXELS_THRESHOLD = PAGE_WIDTH * PAGE_HEIGHT * 6; // ~6 full 22x72 pages worth of pixels

function estimateTotalPixels(imgs) {
  try {
    return imgs.reduce((sum, img) => sum + Math.max(1, Math.round(img.widthIn * DPI)) * Math.max(1, Math.round(img.heightIn * DPI)) * Math.max(1, img.quantity || 1), 0);
  } catch (_) { return Infinity; }
}

function makeSummaryPreview(pageNum, totalPages, count) {
  const c = document.createElement('canvas');
  c.width = 960; c.height = 540; // small, lightweight placeholder
  const x = c.getContext('2d');
  x.fillStyle = '#f4f4f4'; x.fillRect(0, 0, c.width, c.height);
  x.fillStyle = '#5A6872'; x.font = '28px Arial, sans-serif';
  x.fillText(`DTF Sheet Preview`, 40, 70);
  x.font = '22px Arial, sans-serif';
  x.fillText(`Page ${pageNum} of ${totalPages}`, 40, 120);
  x.fillText(`Items on pages: ${count}`, 40, 160);
  x.fillStyle = '#94C940'; x.fillRect(40, 200, 880, 6);
  x.fillStyle = '#5A6872';
  x.font = '18px Arial, sans-serif';
  x.fillText('Preview minimized due to large job size. Final sheets will be generated on submit.', 40, 250);
  return c.toDataURL('image/png');
}


const ImageArranger = forwardRef(function ImageArranger({ onPreviewGenerated, images, setImages, onProceed }, ref) {
  console.log('[ImageArranger] Component mounted');
  const [selectedCanvas, setSelectedCanvas] = useState(null);
  const [pageCanvases, setPageCanvases] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [userRate, setUserRate] = useState(0.04);
  const [isArranging, setIsArranging] = useState(false);
  const [isUploadingSheets, setIsUploadingSheets] = useState(false);
  const [lastUploadedSheetUrls, setLastUploadedSheetUrls] = useState([]);
  const [uploadProgressCount, setUploadProgressCount] = useState(0);
  const [uploadProgressTotal, setUploadProgressTotal] = useState(0);
  const canvasContainerRef = useRef(null);
  const [cropNotice, setCropNotice] = useState("");

  useEffect(() => {
    console.log('[ImageArranger] Subscribing to user rate');
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const userRef = doc(db, "users", user.uid);
      const unsubscribe = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.rate) {
            console.log('[ImageArranger] User rate changed →', data.rate);
            setUserRate(parseFloat(data.rate));
          }
        }
      });
      return () => unsubscribe();
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const n = Math.max(1, Number(e?.detail?.count) || 1);
      setCropNotice('Whitespace cropped automatically on ' + n + ' image' + (n > 1 ? 's' : '') + '.');
      setTimeout(() => setCropNotice(''), 2500);
    };
    document.addEventListener('sodtf-crop-toast', handler);
    return () => document.removeEventListener('sodtf-crop-toast', handler);
  }, []);

  const handleImageSizeChange = (index, newWidthIn, newHeightIn) => {
    console.log('[ImageArranger] handleImageSizeChange', { index, newWidthIn, newHeightIn });
    setImages((prevImages) =>
      prevImages.map((img, i) =>
        i === index ? { ...img, widthIn: newWidthIn, heightIn: newHeightIn } : img
      )
    );
  };

  const createNewPage = (pageNum, container) => {
    console.log('[ImageArranger] createNewPage →', pageNum);
    const canvas = document.createElement("canvas");
    canvas.onclick = () => setSelectedCanvas(canvas);
    canvas.className = "canvas-page";
    canvas.style.border = "1px solid #aaa";
    // Constrain preview to container, actual size set after arranging
    canvas.style.maxWidth = '100%';
    canvas.style.maxHeight = '100%';
    // Always enforce fixed 22in width, even empty
    canvas.style.width = `${SHEET_WIDTH_IN}in`;
    canvas.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
    canvas.width = PAGE_WIDTH;
    canvas.height = PAGE_HEIGHT;
    // Do NOT set canvas.style.height here; it will be set after trimming.

    const canvasContainer = document.createElement("div");
    canvasContainer.style.position = "relative";
    canvasContainer.style.display = "flex";
    canvasContainer.style.flexDirection = "column";
    canvasContainer.style.alignItems = "center";
    // Enforce fixed 22in container width to match canvas
    canvasContainer.style.width = `${SHEET_WIDTH_IN}in`;
    canvasContainer.appendChild(canvas);
    container.appendChild(canvasContainer);

    return { canvas, ctx: canvas.getContext("2d") };
  };

  const handleImageUpload = (e) => {
    console.log('[ImageArranger] handleImageUpload: files =', e?.target?.files?.length || 0);
    const files = Array.from(e.target.files);
    let croppedCount = 0;
    const imagePromises = files.map((file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          // Attempt to trim transparent borders
          const trim = trimTransparentBorder(img, 10);
          const finish = (srcUrl, pxW, pxH) => resolve({
            src: srcUrl,
            fileName: file.name,
            quantity: 1,
            widthIn: pxW / DPI,
            heightIn: pxH / DPI,
            name: file.name.split(".")[0],
          });
          if (trim && trim.changed && trim.canvas) {
            try {
              croppedCount += 1;
              trim.canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                finish(url, trim.canvas.width, trim.canvas.height);
              }, 'image/png');
            } catch (_) {
              finish(img.src, img.width, img.height);
            }
          } else {
            finish(img.src, img.width, img.height);
          }
        };
        img.src = URL.createObjectURL(file);
      });
    });

    Promise.all(imagePromises).then((loaded) => {
      console.log('[ImageArranger] handleImageUpload: loaded =', loaded.length);
      setImages((prev) => [...prev, ...loaded]);
      if (croppedCount > 0) {
        setCropNotice(`Whitespace cropped automatically on ${croppedCount} image${croppedCount > 1 ? 's' : ''}.`);
        setTimeout(() => setCropNotice(""), 2500);
      }
    });
  };

  // Helper for laying out and drawing pages using MaxRectsPacker
  async function layoutAndDrawPages() {
    console.log('[ImageArranger] layoutAndDrawPages: start images =', images.length);
    const container = canvasContainerRef.current;
    container.innerHTML = "";
    const frag = document.createDocumentFragment();
    const pages = [];

    const IMAGE_PADDING = 0;
    const MARGIN_PX = Math.round(MARGIN * DPI);

    const allInstances = [];
    images.forEach(img => {
      for (let i = 0; i < img.quantity; i++) allInstances.push(img);
    });

    console.log('[ImageArranger] Packer init', { PAGE_WIDTH, PAGE_HEIGHT, GAP, MARGIN_PX });
    const packer = new MaxRectsPacker(
      Math.max(1, PAGE_WIDTH - 2 * MARGIN_PX),
      Math.max(1, PAGE_HEIGHT - 2 * MARGIN_PX),
      GAP,
      { smart: true, pot: false, allowRotation: true }
    );

    const rectsToPack = allInstances.map((img, i) => {
      const w = Math.round(img.widthIn * DPI);
      const h = Math.round(img.heightIn * DPI);
      return { width: w, height: h, data: { img } };
    });

    packer.addArray(rectsToPack);
    console.log('[ImageArranger] Packer bins =', packer.bins.length);

    packer.bins.forEach((bin, i) => {
      console.log('[ImageArranger] Rendering bin', i + 1, 'rects =', bin.rects.length);
      let pageRef = createNewPage(i + 1, frag);
      let canvas = pageRef.canvas;
      let ctx = canvas.getContext('2d');

      // --- Row-leveling pass (non-rotated identical images) ---
      // If two or more identical images (same name & size) are roughly on the same row
      // we snap their Y to a shared baseline to keep them level. We skip rotated ones entirely.
      const LEVEL_TOL = 12; // px tolerance: only nudge small differences to avoid overlaps
      const groups = new Map();
      bin.rects.forEach(r => {
        if (r.rot) return; // do not move rotated placements
        const key = `${r.data.img.name}|${r.width}x${r.height}|nr`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(r);
      });
      // For each group, make left-to-right clusters of rects that share a similar Y
      // and snap each cluster (with 2+ rects) to the cluster's minimum Y.
      groups.forEach(list => {
        list.sort((a, b) => a.x - b.x);
        let cluster = [];
        let baseline = null;
        const flush = () => {
          if (cluster.length >= 2 && baseline !== null) {
            const targetY = Math.min(...cluster.map(rr => rr.y));
            cluster.forEach(rr => { rr.y = targetY; });
          }
          cluster = [];
          baseline = null;
        };
        list.forEach(r => {
          if (baseline === null) {
            cluster = [r];
            baseline = r.y;
          } else if (Math.abs(r.y - baseline) <= LEVEL_TOL) {
            cluster.push(r);
            baseline = Math.min(baseline, r.y);
          } else {
            flush();
            cluster = [r];
            baseline = r.y;
          }
        });
        flush();
      });

      bin.rects.forEach(rect => {
        const imgObj = rect.data.img;
        const w = rect.width;
        const h = rect.height;
        const rotated = !!rect.rot; // maxrects-packer sets rot when rotated placement is used

        const cacheKey = `${imgObj.name}_${rotated ? h : w}_${rotated ? w : h}_${rotated}`;
        let renderedCanvas;

        if (imageCache.has(cacheKey)) {
          renderedCanvas = imageCache.get(cacheKey);
        } else {
          const offCanvas = document.createElement("canvas");
          offCanvas.width = rotated ? h : w;
          offCanvas.height = rotated ? w : h;
          const offCtx = offCanvas.getContext("2d");
          if (rotated) {
            // draw the source rotated 90deg CW
            offCtx.save();
            offCtx.translate(offCanvas.width, 0);
            offCtx.rotate(Math.PI / 2);
            offCtx.drawImage(imgObj.image, 0, 0, w, h);
            offCtx.restore();
          } else {
            offCtx.drawImage(imgObj.image, 0, 0, w, h);
          }
          imageCache.set(cacheKey, offCanvas);
          if (imageCache.size > 600) { imageCache.clear(); }
          renderedCanvas = offCanvas;
        }

        const drawX = rect.x + MARGIN_PX;
        const drawY = rect.y + MARGIN_PX + IMAGE_PADDING / 2;
        ctx.drawImage(renderedCanvas, drawX, drawY, renderedCanvas.width, renderedCanvas.height);
      });

      // Trim to actual size based on content bounds
      const contentMaxY = bin.rects.reduce((max, rect) => Math.max(max, rect.y + rect.height + IMAGE_PADDING), 0);
      const maxY = Math.min(PAGE_HEIGHT, MARGIN_PX + contentMaxY + MARGIN_PX);
      // Draw-only trim to avoid large getImageData/putImageData copies
      const trimmed = document.createElement('canvas');
      trimmed.width = canvas.width;
      trimmed.height = maxY;
      const tctx = trimmed.getContext('2d');
      tctx.drawImage(canvas, 0, 0, canvas.width, maxY, 0, 0, canvas.width, maxY);
      canvas.replaceWith(trimmed);
      canvas = trimmed;
      ctx = canvas.getContext('2d');
      canvas.style.width = `${SHEET_WIDTH_IN}in`;
      canvas.style.height = `${canvas.height / DPI}in`;
      console.log('[ImageArranger] Page', i + 1, 'trimmed height(px)=', maxY, 'height(in)=', roundSize(canvas.height / DPI).toFixed(2));
      pages.push(canvas);
    });

    container.appendChild(frag);
    setPageCanvases(pages);

    console.log('[ImageArranger] layoutAndDrawPages: done pages =', pages.length);
    return pages;
  }

  // Debounce utility for arrangeAllPages
  let arrangeTimeout;
  const debounceArrangeAllPages = (callback, delay = 300) => {
    clearTimeout(arrangeTimeout);
    arrangeTimeout = setTimeout(callback, delay);
  };

  // Returns a promise that resolves to { previewImages, totalCanvasArea }
  const arrangeAllPages = async (embedMetadata = false) => {
    console.log('[ImageArranger] arrangeAllPages: start embedMetadata =', embedMetadata);
    imageCache.clear();
    // --- Mobile short-circuit: do not build sheets on mobile/iOS ---
    if (isMobile() || isIOS()) {
      console.log('[ImageArranger] Mobile detected → skipping sheet build entirely');
      const totalPictureArea = images.reduce(
        (sum, img) => sum + (roundSize(img.widthIn) * roundSize(img.heightIn) * img.quantity),
        0
      );
      setIsArranging(false);
      return { previewImages: [], totalCanvasArea: totalPictureArea, previewMode: 'mobile' };
    }
    // fast path: minimal logging for performance
    // ---- Fallback gate: skip heavy drawing for very large jobs ----
    const totalInstances = images.reduce((s, img) => s + Math.max(1, img.quantity || 1), 0);
    const estPixels = estimateTotalPixels(images);
    const needsFallback = totalInstances > FALLBACK_INSTANCE_THRESHOLD || estPixels > FALLBACK_PIXELS_THRESHOLD;
    if (needsFallback) {
      console.warn('[ImageArranger] Safe mode fallback', { totalInstances, estPixels });
      const estPages = Math.max(1, Math.ceil((estPixels || 0) / (PAGE_WIDTH * PAGE_HEIGHT)));
      const previewImages = Array.from({ length: Math.min(estPages, 6) }, (_, i) => makeSummaryPreview(i + 1, estPages, totalInstances));
      const totalPictureArea = images.reduce(
        (sum, img) => sum + (roundSize(img.widthIn) * roundSize(img.heightIn) * img.quantity),
        0
      );
      console.warn(`⚠️ Safe mode fallback engaged: ${totalInstances} instances, estPixels=${estPixels}`);
      // Clear container and show small note
      const container = canvasContainerRef.current;
      if (container) {
        container.innerHTML = '';
        const note = document.createElement('div');
        note.style.padding = '12px';
        note.style.fontFamily = 'Anek Latin, Arial, sans-serif';
        note.style.color = '#5A6872';
        note.textContent = 'Preview minimized due to large job size. You can still proceed to checkout.';
        container.appendChild(note);
      }
      setIsArranging(false);
      console.log('[ImageArranger] Safe previews generated =', previewImages.length);
      return { previewImages, totalCanvasArea: totalPictureArea, previewMode: 'safe' };
    }
    // Ensure each image object has a loaded Image element before drawing
    await Promise.all(
      images.map(img => {
        if (!img.image) {
          return new Promise(resolve => {
            const imageEl = new window.Image();
            imageEl.crossOrigin = "anonymous";
            imageEl.onload = () => {
              img.image = imageEl;
              resolve();
            };
            imageEl.onerror = () => {
              console.warn("Image failed to load for drawing:", img);
              resolve();
            };
            imageEl.src = img.src || img.url;
          });
        }
        return Promise.resolve();
      })
    );
    // Auto-crop transparent borders after load, once per image
    try {
      let croppedCount = 0;
      images.forEach((img) => {
        if (!img || !img.image || img._cropChecked) return;
        let result = null;
        try { result = trimTransparentBorder(img.image, 10); } catch (_) { result = null; }
        if (result && result.changed && result.canvas) {
          img.image = result.canvas;
          img.widthIn = roundSize(result.canvas.width / DPI);
          img.heightIn = roundSize(result.canvas.height / DPI);
          croppedCount += 1;
        }
        img._cropChecked = true;
      });
      if (croppedCount > 0) {
        setCropNotice('Whitespace cropped automatically on ' + croppedCount + ' image' + (croppedCount > 1 ? 's' : '') + '.');
        setTimeout(() => setCropNotice(""), 2500);
      }
    } catch (e) { /* ignore */ }
    let previewImages = [];
    try {
      // Fast preview path: skip PNG.js metadata embedding
      if (!embedMetadata || isMobile() || isIOS()) {
        console.log('[ImageArranger] Fast preview path');
        // layout and draw only
        const pages = await layoutAndDrawPages();
        const previewImages = pages.map((canvas) => {
          try { return canvas.toDataURL(); } catch (_) { return null; }
        }).filter(Boolean);
        console.log('[ImageArranger] Fast previews =', previewImages.length);
        // append canvases (wrap each in a fresh container to avoid null parent)
        const container = canvasContainerRef.current;
        container.innerHTML = "";
        const frag = document.createDocumentFragment();
        pages.forEach((cv) => {
          const wrapper = document.createElement('div');
          wrapper.style.position = 'relative';
          wrapper.style.display = 'flex';
          wrapper.style.flexDirection = 'column';
          wrapper.style.alignItems = 'center';
          wrapper.style.width = `${SHEET_WIDTH_IN}in`;
          wrapper.appendChild(cv);
          frag.appendChild(wrapper);
        });
        container.appendChild(frag);
        // Show trimmed canvases
        pages.forEach((canvas) => {
          // Enforce fixed width, dynamic height
          canvas.style.width = `${SHEET_WIDTH_IN}in`;
          canvas.style.height = `${canvas.height / DPI}in`;
        });
        // Store just the canvas elements for preview navigation
        setPageCanvases([...pages]);
        console.log(`[ImageArranger] Redraw complete: ${pages.length} page(s) arranged`);
        // Compute total area of all pictures (square inches)
        const totalPictureArea = images.reduce(
          (sum, img) => sum + (roundSize(img.widthIn) * roundSize(img.heightIn) * img.quantity),
          0
        );
        setIsArranging(false);
        return { previewImages, totalCanvasArea: totalPictureArea, previewMode: 'full' };
      }

      // --- Metadata-embedding path (PNG.js) ---
      const IMAGE_PADDING = 10;
      const MARGIN_X = Math.round(MARGIN * DPI);
      const MARGIN_Y = Math.round(MARGIN * DPI);
      const container = canvasContainerRef.current;
      container.innerHTML = "";
      const frag = document.createDocumentFragment();
      let pageNum = 1;
      let { canvas, ctx } = createNewPage(pageNum, frag);
      let cursorX = MARGIN_X, cursorY = MARGIN_Y, rowHeight = 0;
      const pages = [{ canvas, ctx, pageMaxY: MARGIN_Y, pageMaxX: 0 }];
      const drawImage = (imgObj) => {
        const w = Math.round(imgObj.widthIn * DPI);
        const h = Math.round(imgObj.heightIn * DPI);
        let rotated = false;
        // Only add GAP between items, not after the last item in a row
        const gapIfNeeded = cursorX > MARGIN_X ? GAP : 0;
        const fitsWidth = (needW) => cursorX + gapIfNeeded + needW <= (PAGE_WIDTH - MARGIN_X);
        if (!fitsWidth(w)) {
          // Try rotated if that helps width AND height constraints
          if (fitsWidth(h) && (cursorY + w <= PAGE_HEIGHT - MARGIN_Y)) {
            rotated = true;
          } else {
            // New row
            cursorX = MARGIN_X;
            cursorY += rowHeight + GAP;
            rowHeight = 0;
          }
        }
        if ((rotated ? cursorY + w : cursorY + h) > PAGE_HEIGHT - MARGIN_Y) {
          pageNum++;
          ({ canvas, ctx } = createNewPage(pageNum, frag));
          cursorX = MARGIN_X;
          cursorY = MARGIN_Y;
          rowHeight = 0;
          pages.push({ canvas, ctx, pageMaxY: MARGIN_Y, pageMaxX: 0 });
        }
        const cacheKey = `${imgObj.name}_${w}_${h}_${rotated}`;
        let renderedCanvas;
        if (imageCache.has(cacheKey)) {
          renderedCanvas = imageCache.get(cacheKey);
        } else {
          const offCanvas = document.createElement("canvas");
          offCanvas.width = rotated ? h : w;
          offCanvas.height = rotated ? w : h;
          const offCtx = offCanvas.getContext("2d");
          if (rotated) {
            offCtx.save();
            offCtx.translate(offCanvas.width, 0);
            offCtx.rotate(Math.PI / 2);
            offCtx.drawImage(imgObj.image, 0, 0, w, h);
            offCtx.restore();
          } else {
            offCtx.drawImage(imgObj.image, 0, 0, w, h);
          }
          imageCache.set(cacheKey, offCanvas);
          if (imageCache.size > 600) { imageCache.clear(); }
          renderedCanvas = offCanvas;
        }
        const drawX = cursorX + gapIfNeeded + IMAGE_PADDING / 2;
        const drawY = cursorY + IMAGE_PADDING / 2;
        ctx.drawImage(renderedCanvas, drawX, drawY, renderedCanvas.width, renderedCanvas.height);
        const drawW = renderedCanvas.width;
        const drawH = renderedCanvas.height;
        const page = pages[pages.length - 1];
        page.pageMaxY = Math.max(page.pageMaxY, drawY + drawH + IMAGE_PADDING);
        page.pageMaxX = Math.max(page.pageMaxX, drawX + drawW);
        // advance cursor only by the drawn width (no trailing gap at row end)
        cursorX = drawX + drawW;
        rowHeight = Math.max(rowHeight, drawH + IMAGE_PADDING);
      };
      let allInstances = [];
      images.forEach((imgObj) => {
        for (let i = 0; i < imgObj.quantity; i++) {
          allInstances.push(imgObj);
        }
      });
      allInstances.sort((a, b) => {
        const areaA = a.widthIn * a.heightIn;
        const areaB = b.widthIn * b.heightIn;
        return areaB - areaA;
      });
      allInstances.forEach(drawImage);
      console.log('[ImageArranger] Metadata path: pages =', pages.length);
      const previewPromises = pages.map(async (page, idx) => {
        console.log('[ImageArranger] Metadata encode page', idx + 1);
        const { canvas, ctx } = page;
        // Trim pixel buffer to content bounds
        if (isMobile() || isIOS()) {
          const trimmed = document.createElement('canvas');
          trimmed.width = page.pageMaxX;
          trimmed.height = page.pageMaxY + MARGIN_Y;
          const tctx = trimmed.getContext('2d');
          tctx.drawImage(canvas, 0, 0, page.pageMaxX, page.pageMaxY + MARGIN_Y, 0, 0, page.pageMaxX, page.pageMaxY + MARGIN_Y);
          canvas.replaceWith(trimmed);
          page.canvas = trimmed;
          page.ctx = trimmed.getContext('2d');
        } else {
          const prevImage = ctx.getImageData(0, 0, canvas.width, canvas.height);
          canvas.width = page.pageMaxX;
          canvas.height = page.pageMaxY + MARGIN_Y;
          ctx.putImageData(prevImage, 0, 0);
        }
        // Enforce fixed width, dynamic height for display
        page.canvas.style.width = `${SHEET_WIDTH_IN}in`;
        page.canvas.style.height = `${page.canvas.height / DPI}in`;
        page.canvas.style.maxWidth = '100%';
        page.canvas.style.maxHeight = '100%';
        return new Promise((resolve) => {
          page.canvas.toBlob(async (blob) => {
            const arrayBuffer = await blob.arrayBuffer();
            const buf = Buffer.from(new Uint8Array(arrayBuffer));
            const png = PNG.sync.read(buf);
            png.ppuX = PPU_X;
            png.ppuY = PPU_Y;
            png.physUnit = PHYS_UNIT;
            const outputBuffer = PNG.sync.write(png);
            const uint8 = new Uint8Array(outputBuffer);
            const newBlob = new Blob([uint8], { type: 'image/png' });
            const dataUrl = URL.createObjectURL(newBlob);
            resolve(dataUrl);
          }, 'image/png');
        });
      });
      previewImages = await Promise.all(previewPromises);
      console.log('[ImageArranger] Metadata previews ready =', previewImages.length);
      container.appendChild(frag);
      pages.forEach(({ canvas }, idx) => {
        const widthInches = roundSize((canvas.width / PAGE_WIDTH) * SHEET_WIDTH_IN).toFixed(2);
        const heightInches = roundSize((canvas.height / PAGE_HEIGHT) * SHEET_HEIGHT_IN).toFixed(2);
        console.log(`Canvas page ${idx + 1}: ${canvas.width}×${canvas.height}px (${widthInches}in × ${heightInches}in)`);
      });
      console.log(`Pixels-per-unit (pHYs): ${PPU_X} px/m (X), ${PPU_Y} px/m (Y), unit=${PHYS_UNIT}`);
      setPageCanvases(pages.map(p => p.canvas));
      console.log(`Redraw complete: ${pages.length} page(s) arranged`);
      const totalPictureArea = images.reduce(
        (sum, img) => sum + (roundSize(img.widthIn) * roundSize(img.heightIn) * img.quantity),
        0
      );
      const rate = () => userRate;
      const totalCost = images.reduce((sum, img) => {
        const widthHeight = Number(img.widthIn) * Number(img.heightIn);
        const rateTotal = roundMoney(widthHeight * rate());
        const quantityTotal = roundMoney(rateTotal * Number(img.quantity));

        console.log(
          `📐 ${img.name || "Unnamed"}: ${roundMoney(widthHeight).toFixed(2)}in² × $${roundMoney(rate()).toFixed(2)} × ${img.quantity} = $${roundMoney(quantityTotal).toFixed(2)}`
        );

        return roundMoney(sum + quantityTotal);
      }, 0);
      console.log("Total Cost:", roundMoney(totalCost));
      console.log("Total Square Inches:", totalPictureArea);
      return { previewImages, totalCanvasArea: totalPictureArea, previewMode: 'full' };
    } catch (e) {
      console.error('Arrange failed; falling back to safe previews:', e);
      const totalInstances = images.reduce((s, img) => s + Math.max(1, img.quantity || 1), 0);
      const estPixels = estimateTotalPixels(images);
      const estPages = Math.max(1, Math.ceil((estPixels || 0) / (PAGE_WIDTH * PAGE_HEIGHT)));
      const previewImages = Array.from({ length: Math.min(estPages, 4) }, (_, i) => makeSummaryPreview(i + 1, estPages, totalInstances));
      const totalPictureArea = images.reduce(
        (sum, img) => sum + (roundSize(img.widthIn) * roundSize(img.heightIn) * img.quantity),
        0
      );
      setIsArranging(false);
      return { previewImages, totalCanvasArea: totalPictureArea, previewMode: 'safe' };
    }
  };

  // --- Background upload of arranged sheets: base64 data URLs only ---
  // Dedupe identical data URLs: upload uniques, return { urls, groups }
  async function uploadSheetsWithDpi(previewImages) {
    console.log('[ImageArranger] uploadSheetsWithDpi: start count =', (previewImages || []).length);
    try {
      setIsUploadingSheets(true);
      // Build unique set and counts to avoid redundant uploads
      const counts = new Map();
      const uniques = [];
      (previewImages || []).forEach((d) => {
        if (!d) return;
        const key = String(d);
        counts.set(key, (counts.get(key) || 0) + 1);
        if (counts.get(key) === 1) uniques.push(d);
      });
      // Print a concise summary of quantities detected per unique sheet
      console.log('[ImageArranger] Dedupe summary:', {
        totalSheets: (previewImages || []).length,
        uniqueSheets: uniques.length,
      });
      uniques.forEach((u, idx) => {
        const qty = counts.get(String(u)) || 1;
        console.log(`[ImageArranger] Sheet #${idx + 1} quantity: ${qty}`);
      });
      setUploadProgressTotal(uniques.length);
      setUploadProgressCount(0);
      const auth = getAuth();
      const uid = auth.currentUser?.uid || 'anonymous';
      const storage = getStorage();
      // Clean up any previously uploaded sheets to avoid storage bloat
      try {
        const pathFromStorageUrl = (u) => {
          try {
            if (typeof u !== 'string') return '';
            if (u.startsWith('gs://')) {
              const parsed = new URL(u);
              return parsed.pathname.replace(/^\//, '');
            }
            if (u.includes('firebasestorage.googleapis.com')) {
              const parsed = new URL(u);
              const parts = parsed.pathname.split('/');
              const oIndex = parts.indexOf('o');
              const encoded = parts[oIndex + 1];
              return decodeURIComponent(encoded || '');
            }
            return u;
          } catch (_) { return ''; }
        };
        if (Array.isArray(lastUploadedSheetUrls) && lastUploadedSheetUrls.length) {
          console.log('[ImageArranger] Cleaning up previously uploaded sheets:', lastUploadedSheetUrls.length);
          await Promise.all(
            lastUploadedSheetUrls.map(async (url) => {
              try {
                const objRef = storageRef(storage, pathFromStorageUrl(url) || url);
                await deleteObject(objRef);
              } catch (e) {
                console.warn('Failed to delete previous sheet from Storage:', url, e?.message || e);
              }
            })
          );
        }
      } catch (e) {
        console.warn('[ImageArranger] Cleanup step failed:', e?.message || e);
      }
      const uploads = (uniques || []).map(async (dataUrl, i) => {
        if (!dataUrl) return null;
        const qtyForThis = counts.get(String(dataUrl)) || 1;
        console.log('[ImageArranger] Uploading unique sheet', i + 1, 'of', uniques.length, '| quantity =', qtyForThis);
        let dpiUrl = dataUrl;
        try {
          dpiUrl = await changeDpiDataUrl(dataUrl, 300);
        } catch (e) {
          console.warn('changeDpiDataUrl failed for sheet', i, e);
          dpiUrl = dataUrl; // fallback
        }
        const filePath = `orders/${uid}/${Date.now()}_${i}.png`;
        const objRef = storageRef(storage, filePath);
        await uploadString(objRef, dpiUrl, 'data_url');
        const url = await getDownloadURL(objRef);
        setUploadProgressCount((c) => c + 1);
        console.log('[ImageArranger] Uploaded sheet', i + 1, '→', url);
        return url;
      });
      const urls = (await Promise.all(uploads)).filter(Boolean);
      console.log('[ImageArranger] uploadSheetsWithDpi: complete urls =', urls.length);
      // Track the latest uploaded sheet URLs for cleanup on next arrange
      setLastUploadedSheetUrls(urls);
      // Build groups mapping unique URL to count
      const keyToUrl = new Map();
      let u = 0;
      (previewImages || []).forEach((d) => {
        const key = String(d);
        if (!keyToUrl.has(key)) keyToUrl.set(key, urls[u++]);
      });
      const seen = new Set();
      const groups = [];
      (previewImages || []).forEach((d) => {
        const key = String(d);
        if (seen.has(key)) return;
        seen.add(key);
        const cnt = counts.get(key) || 1;
        const url = keyToUrl.get(key);
        if (url) groups.push({ url, count: cnt });
      });
      // Log final grouped quantities that will be sent back up
      console.log('[ImageArranger] Upload complete. Sheet quantities:', groups.map((g, i) => ({ index: i + 1, urlSample: (typeof g.url === 'string' ? g.url.slice(0, 60) : ''), quantity: g.count })));
      return { urls, groups };
    } finally {
      setIsUploadingSheets(false);
    }
  }

  // Build real arranged sheets headlessly (no UI) for large jobs (safe mode)
  async function buildSheetsHeadlesslyForSafeMode(imgList) {
    try {
      // Preload images into HTMLImageElement for drawing
      await Promise.all(
        (imgList || []).map(img => {
          if (img && img.__safeImageEl) return Promise.resolve();
          return new Promise(resolve => {
            const el = new window.Image();
            el.crossOrigin = 'anonymous';
            el.onload = () => { img.__safeImageEl = el; resolve(); };
            el.onerror = () => { console.warn('SafeMode: failed to load image for headless pack', img); resolve(); };
            el.src = img.url || img.src || img.file;
          });
        })
      );

      const PAGE_WIDTH = SHEET_WIDTH_IN * DPI;
      const PAGE_HEIGHT = SHEET_HEIGHT_IN * DPI;
      const GAP = 225;

      // Expand by quantity
      const instances = [];
      (imgList || []).forEach(img => {
        const q = Math.max(1, Number(img.quantity) || 1);
        for (let i = 0; i < q; i++) instances.push(img);
      });

      // Pack
      const rects = instances.map((p, i) => ({
        width: Math.round((Number(p.widthIn) || 0) * DPI),
        height: Math.round((Number(p.heightIn) || 0) * DPI),
        data: { idx: i }
      }));
      const packer = new MaxRectsPacker(PAGE_WIDTH, PAGE_HEIGHT, GAP, { smart: true, pot: false, allowRotation: true });
      packer.addArray(rects);

      // Draw sheets
      const dataUrls = [];
      for (const bin of packer.bins) {
        const c = document.createElement('canvas');
        c.width = PAGE_WIDTH; c.height = PAGE_HEIGHT;
        const x = c.getContext('2d');
        for (const r of bin.rects) {
          const p = instances[r.data.idx];
          const el = p && p.__safeImageEl;
          if (!el) continue;
          const w = r.width, h = r.height;
          if (r.rot) {
            x.save(); x.translate(r.x + h, r.y); x.rotate(Math.PI / 2); x.drawImage(el, 0, 0, w, h); x.restore();
          } else {
            x.drawImage(el, r.x, r.y, w, h);
          }
        }
        const maxY = bin.rects.reduce((m, r) => Math.max(m, r.y + r.height), 0);
        const trimmed = document.createElement('canvas');
        trimmed.width = c.width; trimmed.height = Math.max(1, maxY);
        trimmed.getContext('2d').drawImage(c, 0, 0, c.width, trimmed.height, 0, 0, c.width, trimmed.height);
        try {
          dataUrls.push(trimmed.toDataURL('image/png'));
        } catch (e) {
          console.warn('SafeMode: toDataURL failed for a sheet', e);
        }
      }
      return dataUrls;
    } catch (e) {
      console.error('SafeMode headless build failed:', e);
      return [];
    }
  }


  useImperativeHandle(ref, () => ({
    arrange: async () => {
      console.log('[ImageArranger] arrange(): pictures-only mode start');
      try {
        setIsArranging(true);
        // cooldown no longer needed in pictures-only mode

        const totalPictureArea = images.reduce((sum, img) => {
          const widthIn = Number(img.widthIn) || 0;
          const heightIn = Number(img.heightIn) || 0;
          const qty = Math.max(1, Number(img.quantity) || 1);
          return sum + (widthIn * heightIn * qty);
        }, 0);

        const rate = () => userRate;
        const totalCostRaw = images.reduce((sum, img) => {
          const widthHeight = Number(img.widthIn) * Number(img.heightIn);
          const quantityTotal = roundMoney(Math.max(0, widthHeight) * rate() * Math.max(1, Number(img.quantity) || 1));
          return roundMoney(sum + quantityTotal);
        }, 0);

        const pictures = images.map((img) => {
          const src = img.url || img.src || img.file;
          const fallbackName = nameFromFirebaseUrl(src) || 'Unnamed';
          const name = img.name || fallbackName;
          return {
            quantity: Math.max(1, Number(img.quantity) || 1),
            widthIn: roundSize(Number(img.widthIn) || 0).toFixed(2),
            heightIn: roundSize(Number(img.heightIn) || 0).toFixed(2),
            name,
            file: src,
          };
        });

        if (typeof onPreviewGenerated === 'function') {
          const payload = {
            previewImages: [],
            totalCanvasArea: totalPictureArea,
            totalPictureArea,
            totalCost: roundMoney(totalCostRaw),
            totalImages: pictures.reduce((sum, pic) => sum + Math.max(1, Number(pic.quantity) || 1), 0),
            images,
            pictures,
            previewMode: 'pictures-only',
          };
          console.log('[ImageArranger] onPreviewGenerated(): pictures-only payload ready');
          onPreviewGenerated(payload);
        }

        // Clear any leftover sheet previews from previous runs
        setPageCanvases([]);
        setCurrentPageIndex(0);
        setSelectedCanvas(null);
        setIsUploadingSheets(false);
        setUploadProgressCount(0);
        setUploadProgressTotal(0);
      } catch (e) {
        console.error('Arrange failed in pictures-only mode:', e);
        if (typeof onPreviewGenerated === 'function') {
          onPreviewGenerated({
            previewImages: [],
            totalCanvasArea: 0,
            previewMode: 'pictures-only',
            totalCost: 0,
            totalImages: 0,
            images,
            pictures: [],
          });
        }
      } finally {
        setIsArranging(false);
      }
    },
    exportArrange: async () => {
      console.log('[ImageArranger] exportArrange(): pictures-only mode returning empty list');
      return [];
    }
  }));

  return (
    <>
      {!!cropNotice && (
        <Box sx={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1f2937', color: 'white', px: 2, py: 1, borderRadius: 1.5,
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)', zIndex: 1400, fontFamily: 'Anek Latin'
        }}>
          {cropNotice}
        </Box>
      )}
      {(isArranging || isUploadingSheets) && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}>
          {/* loader ring + glow + logo (same look as Checkout) */}
          <Box sx={{ position: 'relative', width: 180, height: 180, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: 'conic-gradient(from 0deg, rgba(148,201,64,0) 0%, rgba(148,201,64,.9) 25%, rgba(148,201,64,0) 50%, rgba(148,201,64,.9) 75%, rgba(148,201,64,0) 100%)',
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 14px), #000 0)',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 14px), #000 0)',
              animation: 'sodtf_spin 1.6s linear infinite',
              filter: 'drop-shadow(0 0 12px rgba(148,201,64,0.6))'
            }} />
            <Box sx={{ position: 'absolute', inset: 18, borderRadius: '50%', background: 'radial-gradient(circle at 50% 50%, rgba(148,201,64,0.25), rgba(148,201,64,0) 60%)', filter: 'blur(8px)' }} />
            <Box sx={{ position: 'relative', width: 120, height: 120, borderRadius: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={'/logo2.png'} alt="SODTF logo" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
              <Box sx={{
                position: 'absolute', top: -120, left: -60, width: '80px', height: '320px',
                transform: 'rotate(25deg)',
                background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.8) 50%, rgba(255,255,255,0) 100%)',
                mixBlendMode: 'screen',
                animation: 'sodtf_shine 1.8s ease-in-out infinite'
              }} />
            </Box>
          </Box>

          {/* pulsing dots */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#94C940', animation: 'sodtf_pulse 1.2s ease-in-out infinite', animationDelay: '0s' }} />
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#94C940', animation: 'sodtf_pulse 1.2s ease-in-out infinite', animationDelay: '.2s' }} />
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#94C940', animation: 'sodtf_pulse 1.2s ease-in-out infinite', animationDelay: '.4s' }} />
          </Box>

          {/* keyframes (local to this component) */}
          <style>{`
      @keyframes sodtf_spin { to { transform: rotate(360deg); } }
      @keyframes sodtf_pulse { 0%,100% { opacity:.45; transform: scale(1); } 50% { opacity:1; transform: scale(1.06); } }
      @keyframes sodtf_shine { 0% { transform: translateX(-140px) rotate(25deg); } 100% { transform: translateX(250px) rotate(25deg); } }
    `}</style>

          {/* messages */}
          <Typography variant="h6">
            {isArranging ? 'Building sheet previews…' : 'Uploading arranged sheets…'}
          </Typography>
          <Typography variant="h6">
            {isArranging
              ? 'Hang tight — large images can take a minute to render.'
              : 'This can take a bit for large jobs. Please don’t close the tab.'}
          </Typography>

          {/* upload progress count */}
          {isUploadingSheets && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1">
                Uploading sheets: {uploadProgressCount} / {uploadProgressTotal}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        {isArranging && (
          <Box sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(76, 175, 80, 0.8)",
            zIndex: 5000,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}>
            <img src={"/logo2.png"} alt="Logo" style={{ width: 120, marginBottom: 20 }} />
            <CircularProgress size={60} style={{ color: "white" }} />
          </Box>
        )}
        <ImageControlPanel
          images={images}
          setImages={setImages}
          onImageSizeChange={handleImageSizeChange}
          onProceedClick={async () => {
            setIsArranging(true); // Ensure overlay shows immediately
            if (ref.current?.arrange) {
              await ref.current.arrange();
            }
            if (typeof onProceed === 'function') {
              await onProceed();
            }
          }}
        />
        <Box sx={{ flexGrow: 1, p: 2 }}>
          <div
            ref={canvasContainerRef}
            style={(isMobile() || isIOS())
              ? { position: 'absolute', left: '-99999px', top: 0, width: 0, height: 0, overflow: 'hidden' }
              : { display: 'none' }}
          />
          {pageCanvases.length > 0 && (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  border: "1px solid #ccc",
                  display: "inline-block",
                  // Update maxWidth/maxHeight to match scaled preview
                  maxWidth: `${SHEET_WIDTH_IN * DPI * 0.15}px`,
                  maxHeight: `${SHEET_HEIGHT_IN * DPI * 0.15}px`,
                  backgroundColor: '#ababab',
                  backgroundImage: `
      linear-gradient(45deg, #ccc 25%, transparent 25%),
      linear-gradient(-45deg, #ccc 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #ccc 75%),
      linear-gradient(-45deg, transparent 75%, #ccc 75%)
    `,
                  backgroundSize: "20px 20px",
                  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                }}
              >
                <canvas
                  ref={(el) => {
                    if (el && pageCanvases[currentPageIndex]) {
                      const srcCanvas = pageCanvases[currentPageIndex];
                      if (!srcCanvas || !srcCanvas.width || !srcCanvas.height) return;
                      const scale = 0.04;
                      const w = Math.max(1, Math.round(srcCanvas.width * scale));
                      const h = Math.max(1, Math.round(srcCanvas.height * scale));
                      el.width = w;
                      el.height = h;
                      const ctx = el.getContext("2d");
                      if (!ctx) return;
                      ctx.clearRect(0, 0, w, h);
                      ctx.imageSmoothingEnabled = true;
                      ctx.imageSmoothingQuality = "high";
                      try { ctx.drawImage(srcCanvas, 0, 0, w, h); } catch (e) { /* ignore */ }
                    }
                  }}
                />
              </div>
              <div style={{ marginTop: "1rem" }}>
                <Button
                  variant="outlined"
                  onClick={() => setCurrentPageIndex((prev) => Math.max(0, prev - 1))}
                  disabled={currentPageIndex === 0}
                  style={{
                    border: currentPageIndex === 0 ? '2.5px solid #aaa' : '2.5px solid #A7C667',
                    color: currentPageIndex === 0 ? '#aaa' : '#A7C667',
                    backgroundColor: currentPageIndex === 0 ? '#f2f2f2' : 'transparent',
                    marginRight: 15,
                    borderRadius: '10px',
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    cursor: currentPageIndex === 0 ? 'not-allowed' : 'pointer',
                    fontFamily: 'Anek Latin',
                    transition: 'all 0.2s ease-in-out',
                    opacity: currentPageIndex === 0 ? 0.5 : 1,
                  }}
                >
                  BACK
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    setCurrentPageIndex((prev) => Math.min(pageCanvases.length - 1, prev + 1))
                  }
                  disabled={currentPageIndex >= pageCanvases.length - 1}
                  sx={{
                    border: currentPageIndex >= pageCanvases.length - 1 ? '2.5px solid #aaa' : '2.5px solid #A7C667',
                    color: currentPageIndex >= pageCanvases.length - 1 ? '#aaa' : '#A7C667',
                    backgroundColor: currentPageIndex >= pageCanvases.length - 1 ? '#f2f2f2' : 'transparent',
                    borderRadius: '10px',
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    cursor: currentPageIndex >= pageCanvases.length - 1 ? 'not-allowed' : 'pointer',
                    fontFamily: 'Anek Latin',
                    transition: 'all 0.2s ease-in-out',
                    opacity: currentPageIndex >= pageCanvases.length - 1 ? 0.5 : 1,
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Box>
        {selectedCanvas && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 99,
            }}
            onClick={() => setSelectedCanvas(null)}
          >
            <canvas
              ref={(el) => {
                if (el && selectedCanvas) {
                  // draw at native size; CSS will constrain display
                  el.width = selectedCanvas.width;
                  el.height = selectedCanvas.height;
                  const ctx = el.getContext('2d');
                  ctx.clearRect(0, 0, el.width, el.height);
                  ctx.imageSmoothingEnabled = true;
                  ctx.imageSmoothingQuality = 'high';
                  ctx.drawImage(selectedCanvas, 0, 0);
                }
              }}
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                border: "4px solid white",
                boxShadow: "0 0 20px black",
              }}
            />
          </div>
        )}
      </Box>
    </>
  );
});

export default ImageArranger;
